import { useQuery, keepPreviousData } from '@tanstack/react-query';
import InitialEvaluationApi from 'shared/services/initialEvaluationApi';
import CriticalTaskPermitApi from 'shared/services/criticalTaskPermitApi';
import ServiceCardApi from 'shared/services/serviceCardApi';
import type { InitialEvaluation, CriticalTaskPermit } from 'shared/types';
import type { ServiceCard } from 'shared/types/serviceManagement';
import { useAuth } from 'shared/contexts/AuthContext';
import { queryKeys } from '../queryConfig';

// ─── Shared mapped types ───────────────────────────────────────
export type PendingItem = {
  _id: string;
  code: string;
  description: string;
  status: string;
  startDate: string;
  endDate: string;
  companyName: string;
  type: 'evaluation' | 'permit';
  criticalTaskTypes?: string[];
};

export type FichaItem = {
  _id: string;
  code: string;
  description: string;
  status: string;
  startDate: string;
  endDate: string;
  companyName: string;
};

// ─── Mappers ───────────────────────────────────────────────────
const mapEvaluation = (ie: InitialEvaluation): PendingItem => ({
  _id: ie._id,
  code: ie.code,
  description: ie.description,
  status: ie.status,
  startDate: ie.startDate,
  endDate: ie.endDate,
  companyName: typeof ie.company === 'object' ? ie.company.name : '',
  type: 'evaluation',
});

const mapPermit = (ctp: CriticalTaskPermit): PendingItem => ({
  _id: ctp._id,
  code: ctp.code,
  description: ctp.description,
  status: ctp.status,
  startDate: ctp.startDate,
  endDate: ctp.endDate,
  companyName: typeof ctp.company === 'object' ? ctp.company.name : '',
  type: 'permit',
  criticalTaskTypes: ctp.criticalTaskTypes,
});

const mapFicha = (sc: ServiceCard): FichaItem => ({
  _id: sc._id,
  code: sc.code,
  description: sc.description,
  status: sc.status,
  startDate: sc.startDate,
  endDate: sc.endDate,
  companyName: typeof sc.company === 'object' ? sc.company.name : '',
});

const EXCLUDED_STATUS = 'draft';

// ─── Shared pagination params ─────────────────────────────────
export interface ApproverPaginationParams {
  page?: number;
  limit?: number;
}

export interface ApproverPaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ─── Hooks ─────────────────────────────────────────────────────

/**
 * Fetches initial evaluations (excluding drafts) for the approver view.
 *
 * Consolidates the previous 5 per-status API calls into a single request
 * by fetching without a status filter and filtering out drafts client-side.
 */
export const useApproverEvaluations = (
  { page = 1, limit = 10 }: ApproverPaginationParams = {},
  enabled = true,
) => {
  const { user } = useAuth();
  const filters = { page, limit };

  return useQuery({
    queryKey: queryKeys.initialEvaluations.list({ ...filters, scope: 'approver', approverId: user?._id || '' }),
    queryFn: async (): Promise<ApproverPaginatedResult<PendingItem>> => {
      const response = await InitialEvaluationApi.getAll(filters);
      const items = (response.data || [])
        .filter((ie) => ie.status !== EXCLUDED_STATUS)
        .map(mapEvaluation);
      return {
        items,
        total: response.total ?? items.length,
        page: response.page ?? page,
        limit: response.limit ?? limit,
        totalPages: response.totalPages ?? Math.ceil((response.total ?? items.length) / limit),
      };
    },
    enabled,
    placeholderData: keepPreviousData,
  });
};

/**
 * Fetches critical task permits (excluding drafts) for the approver view.
 *
 * Consolidates the previous 6 per-status API calls into a single request.
 */
export const useApproverPermits = (
  { page = 1, limit = 10 }: ApproverPaginationParams = {},
  enabled = true,
) => {
  const { user } = useAuth();
  const filters = { page, limit };

  return useQuery({
    queryKey: queryKeys.criticalTaskPermits.list({ ...filters, scope: 'approver', approverId: user?._id || '' }),
    queryFn: async (): Promise<ApproverPaginatedResult<PendingItem>> => {
      const response = await CriticalTaskPermitApi.getAll(filters);
      const items = (response.data || [])
        .filter((ctp) => ctp.status !== EXCLUDED_STATUS)
        .map(mapPermit);
      return {
        items,
        total: response.total ?? items.length,
        page: response.page ?? page,
        limit: response.limit ?? limit,
        totalPages: response.totalPages ?? Math.ceil((response.total ?? items.length) / limit),
      };
    },
    enabled,
    placeholderData: keepPreviousData,
  });
};

/**
 * Fetches service cards for the approver view.
 */
export const useApproverFichas = (
  { page = 1, limit = 10 }: ApproverPaginationParams = {},
  enabled = true,
) => {
  const { user } = useAuth();
  const filters = { page, limit };

  return useQuery({
    queryKey: queryKeys.serviceCards.list({ ...filters, scope: 'approver', approverId: user?._id || '' }),
    queryFn: async (): Promise<ApproverPaginatedResult<FichaItem>> => {
      const response = await ServiceCardApi.getAll(filters);
      const items = (response.data || []).map(mapFicha);
      return {
        items,
        total: response.total ?? items.length,
        page: response.page ?? page,
        limit: response.limit ?? limit,
        totalPages: response.totalPages ?? Math.ceil((response.total ?? items.length) / limit),
      };
    },
    enabled,
    placeholderData: keepPreviousData,
  });
};
